import * as XLSX from 'xlsx'

import {
  appendStyledSheet,
  calculatePercent,
  capitalizeFirst,
  createReportHeader,
  formatMetric,
} from '@/shared/lib/dashboardExcelFormat'
import {
  formatProductNames,
  formatProductQuantities,
} from '@/shared/lib/exportFormat'

/**
 * Build and download a professional, readable Excel file for the Admin Dashboard.
 */
export function exportAdminDashboardToExcel({
  campaignLeads,
  conversionNumbers,
  ordersClosed,
  ordersLost,
  leadsManagement,
  ordersClosedBySalesperson = [],
  leadsNotFollowedUp = [],
  target = 0,
  achievement = 0,
  period = 'all',
  customRange = {},
} = {}) {
  const workbook = XLSX.utils.book_new()
  const exportedAt = new Date().toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

  // ---------------------------------------------------------------------------
  // 1. EXECUTIVE SUMMARY SHEET
  // ---------------------------------------------------------------------------
  const totalLeads = Number(campaignLeads?.total) || 0
  const wonOrders =
    Number(ordersClosed?.total) || Number(conversionNumbers?.wonCount) || 0
  const lostOrders =
    Number(ordersLost?.total) || Number(conversionNumbers?.lostCount) || 0
  const totalClosed = wonOrders + lostOrders
  const conversionRate = conversionNumbers?.percentage
    ? `${conversionNumbers.percentage}%`
    : calculatePercent(wonOrders, totalClosed || totalLeads)

  const teamTarget = Number(target) || Number(ordersClosed?.target) || 0
  const teamAchieved =
    Number(achievement) ||
    Number(ordersClosed?.achievement) ||
    Number(ordersClosed?.total) ||
    0
  const achievementRate = calculatePercent(teamAchieved, teamTarget)

  const summaryRows = [
    ...createReportHeader({
      title: 'Admin Executive Dashboard',
      subtitle: 'System-wide Pipeline, Sales Targets, Revenue, and Conversions',
      period,
      customRange,
      exportedAt,
    }),
    ['KEY PERFORMANCE INDICATORS (KPIs)'],
    [
      'Category',
      'Metric Name',
      'Performance Value',
      'Context & Benchmark Notes',
    ],
    [
      'Pipeline',
      'Total Leads Created',
      formatMetric(totalLeads, 'Leads'),
      capitalizeFirst(campaignLeads?.subtitle) ||
        'All incoming leads in selected period',
    ],
    [
      'Sales Targets',
      'Team Target Goal',
      formatMetric(teamTarget, 'Units'),
      'Aggregate sales target quota for the team',
    ],
    [
      'Sales Targets',
      'Team Actual Achievement',
      formatMetric(teamAchieved, 'Units'),
      'Total orders successfully won by the team',
    ],
    [
      'Sales Targets',
      'Target Achievement Rate',
      achievementRate,
      'Achieved vs Target goal quota',
    ],
    [
      'Sales Targets',
      'Total Revenue Generated',
      ordersClosed?.revenue != null
        ? `₹ ${Number(ordersClosed.revenue).toLocaleString('en-IN')}`
        : '—',
      'Total revenue generated from closed won deals',
    ],
    [
      'Conversion Funnel',
      'Orders Won (Converted)',
      formatMetric(wonOrders, 'Orders'),
      capitalizeFirst(ordersClosed?.subtitle) ||
        'Deals successfully converted and closed as Won',
    ],
    [
      'Conversion Funnel',
      'Orders Lost',
      formatMetric(lostOrders, 'Orders'),
      capitalizeFirst(ordersLost?.subtitle) ||
        'Deals marked as Lost or dropped',
    ],
    [
      'Conversion Funnel',
      'Total Closed Deals',
      formatMetric(totalClosed, 'Deals'),
      'Sum of won orders and lost orders',
    ],
    [
      'Conversion Funnel',
      'Overall Win / Conversion Rate',
      conversionRate,
      'Won orders share of closed deals',
    ],
    [
      'Team Overview',
      'Active Sales Representatives',
      formatMetric(
        Array.isArray(ordersClosedBySalesperson)
          ? ordersClosedBySalesperson.length
          : 0,
        'Sales Reps',
      ),
      'Sales representatives with deal activity',
    ],
    [
      'Attention Required',
      'Leads Pending Follow-up',
      formatMetric(
        Array.isArray(leadsNotFollowedUp) ? leadsNotFollowedUp.length : 0,
        'Leads',
      ),
      'Active leads awaiting contact or action',
    ],
    [],
    [],
  ]

  // Table 2: Lead Generation by Source
  const sourceItems = campaignLeads?.sources ?? []
  const totalSourceCount = sourceItems.reduce(
    (sum, s) => sum + (Number(s.count) || 0),
    0,
  )

  summaryRows.push(
    ['LEAD GENERATION BY SOURCE CHANNEL'],
    [
      'Metric Group',
      'Lead Source Channel',
      'Leads Count',
      'Share of Total (%)',
    ],
  )
  if (sourceItems.length === 0) {
    summaryRows.push([
      'Lead Source',
      'No source channel data available',
      '0 Leads',
      '0.0%',
    ])
  } else {
    sourceItems.forEach((source) => {
      const count = Number(source.count) || 0
      summaryRows.push([
        'Lead Source',
        capitalizeFirst(source.name),
        formatMetric(count, 'Leads'),
        `${calculatePercent(count, totalSourceCount || totalLeads)} of total leads`,
      ])
    })
    summaryRows.push([
      'Summary Total',
      'TOTAL LEADS GENERATED',
      formatMetric(totalSourceCount || totalLeads, 'Leads'),
      '100.0%',
    ])
  }

  summaryRows.push([], [])

  // Table 3: Leads Pipeline Status Breakdown
  const statusItems = leadsManagement?.status?.items ?? []
  const totalStatusCount = statusItems.reduce(
    (sum, s) => sum + (Number(s.count) || 0),
    0,
  )

  summaryRows.push(
    ['LEADS PIPELINE STATUS BREAKDOWN'],
    [
      'Metric Group',
      'Pipeline Stage / Status',
      'Deals Count',
      'Share of Total (%)',
    ],
  )
  if (statusItems.length === 0) {
    summaryRows.push([
      'Pipeline Status',
      'No status data available',
      '0 Deals',
      '0.0%',
    ])
  } else {
    statusItems.forEach((item) => {
      const count = Number(item.count) || 0
      summaryRows.push([
        'Pipeline Status',
        capitalizeFirst(item.name),
        formatMetric(count, 'Deals'),
        `${calculatePercent(count, totalStatusCount)} of pipeline`,
      ])
    })
    summaryRows.push([
      'Summary Total',
      'TOTAL IN PIPELINE',
      formatMetric(totalStatusCount, 'Deals'),
      '100.0%',
    ])
  }

  summaryRows.push([], [])

  // Table 4: Leads Qualification (Warmth)
  const qualItems = leadsManagement?.qualification?.items ?? []
  const totalQualCount = qualItems.reduce(
    (sum, s) => sum + (Number(s.count) || 0),
    0,
  )

  summaryRows.push(
    ['LEADS QUALIFICATION (WARMTH BREAKDOWN)'],
    ['Metric Group', 'Warmth Level', 'Leads Count', 'Share of Total (%)'],
  )
  if (qualItems.length === 0) {
    summaryRows.push([
      'Qualification',
      'No qualification data available',
      '0 Leads',
      '0.0%',
    ])
  } else {
    qualItems.forEach((item) => {
      const count = Number(item.count) || 0
      summaryRows.push([
        'Qualification',
        capitalizeFirst(item.name),
        formatMetric(count, 'Leads'),
        `${calculatePercent(count, totalQualCount)} of qualified leads`,
      ])
    })
    summaryRows.push([
      'Summary Total',
      'TOTAL QUALIFIED LEADS',
      formatMetric(totalQualCount, 'Leads'),
      '100.0%',
    ])
  }

  appendStyledSheet(workbook, 'Executive Summary', summaryRows)

  // ---------------------------------------------------------------------------
  // 2. SALES TEAM PERFORMANCE SHEET
  // ---------------------------------------------------------------------------
  const salesMap = new Map()

  ;(conversionNumbers?.salespersons ?? []).forEach((person) => {
    salesMap.set(person.name, {
      name: person.name,
      won: Number(person.wonCount ?? person.converted) || 0,
      lost: Number(person.lostCount) || 0,
      total: Number(person.total) || 0,
      percentage: Number(person.percentage) || 0,
      target: 0,
      achievement: 0,
      units: 0,
      products: [],
    })
  })

  ;(ordersClosedBySalesperson ?? []).forEach((sp) => {
    const existing = salesMap.get(sp.name) || {
      name: sp.name,
      won: Number(sp.orders) || 0,
      lost: 0,
      total: 0,
      percentage: 0,
      products: [],
    }
    existing.target = Number(sp.target) || 0
    existing.achievement = Number(sp.achievement ?? sp.orders) || 0
    existing.units = Number(sp.units ?? sp.orders) || 0
    existing.products = Array.isArray(sp.products) ? sp.products : []
    salesMap.set(sp.name, existing)
  })

  const salespersonRows = [
    ...createReportHeader({
      title: 'Sales Team Performance',
      subtitle:
        'Target Quota, Achievement, Won vs Lost Deals, and Products Sold',
      period,
      customRange,
      exportedAt,
    }),
    ['SALESPERSON BREAKDOWN'],
    [
      'Salesperson Name',
      'Target (Units)',
      'Achieved (Units)',
      'Achievement Rate (%)',
      'Orders Won',
      'Orders Lost',
      'Total Assigned Deals',
      'Win Rate (%)',
      'Units Sold',
      'Products Sold Summary',
    ],
  ]

  const reps = Array.from(salesMap.values())
  if (reps.length === 0) {
    salespersonRows.push([
      'No sales activity recorded',
      0,
      0,
      '0.0%',
      0,
      0,
      0,
      '0.0%',
      0,
      '—',
    ])
  } else {
    let sumTarget = 0
    let sumAchieved = 0
    let sumWon = 0
    let sumLost = 0
    let sumTotal = 0
    let sumUnits = 0

    reps.forEach((rep) => {
      sumTarget += rep.target
      sumAchieved += rep.achievement
      sumWon += rep.won
      sumLost += rep.lost
      sumTotal += rep.total
      sumUnits += rep.units

      const productSummary = rep.products?.length
        ? rep.products.map((p) => `${p.name}: ${p.qty}`).join(' | ')
        : '—'

      const winRate =
        rep.total > 0
          ? calculatePercent(rep.won, rep.total)
          : rep.won + rep.lost > 0
            ? calculatePercent(rep.won, rep.won + rep.lost)
            : `${rep.percentage}%`

      salespersonRows.push([
        rep.name,
        rep.target,
        rep.achievement,
        calculatePercent(rep.achievement, rep.target),
        rep.won,
        rep.lost,
        rep.total,
        winRate,
        rep.units,
        productSummary,
      ])
    })

    salespersonRows.push([
      'TEAM TOTAL',
      sumTarget,
      sumAchieved,
      calculatePercent(sumAchieved, sumTarget),
      sumWon,
      sumLost,
      sumTotal,
      calculatePercent(sumWon, sumWon + sumLost || sumTotal),
      sumUnits,
      '—',
    ])
  }

  appendStyledSheet(workbook, 'Sales Team Performance', salespersonRows)

  // ---------------------------------------------------------------------------
  // 3. PRODUCT BREAKDOWN SHEET (WON vs LOST)
  // ---------------------------------------------------------------------------
  const wonProducts = ordersClosed?.products ?? []
  const lostProducts = ordersLost?.products ?? []

  const totalWonUnits = wonProducts.reduce(
    (sum, p) => sum + (Number(p.units ?? p.count) || 0),
    0,
  )
  const totalLostUnits = lostProducts.reduce(
    (sum, p) => sum + (Number(p.units ?? p.count) || 0),
    0,
  )

  const productRows = [
    ...createReportHeader({
      title: 'Product Performance Breakdown',
      subtitle: 'Product Units Sold in Won Deals vs Lost Deals',
      period,
      customRange,
      exportedAt,
    }),
    ['ORDERS WON BY PRODUCT'],
    ['Product Name', 'Units Sold', 'Share of Won Units (%)'],
  ]

  if (wonProducts.length === 0) {
    productRows.push(['No products sold yet', 0, '0.0%'])
  } else {
    wonProducts.forEach((product) => {
      const units = Number(product.units ?? product.count) || 0
      productRows.push([
        product.name,
        units,
        calculatePercent(units, totalWonUnits),
      ])
    })
    productRows.push(['TOTAL WON UNITS', totalWonUnits, '100.0%'])
  }

  productRows.push(
    [],
    [],
    ['ORDERS LOST BY PRODUCT'],
    ['Product Name', 'Units Lost', 'Share of Lost Units (%)'],
  )

  if (lostProducts.length === 0) {
    productRows.push(['No products lost', 0, '0.0%'])
  } else {
    lostProducts.forEach((product) => {
      const units = Number(product.units ?? product.count) || 0
      productRows.push([
        product.name,
        units,
        calculatePercent(units, totalLostUnits),
      ])
    })
    productRows.push(['TOTAL LOST UNITS', totalLostUnits, '100.0%'])
  }

  appendStyledSheet(workbook, 'Product Breakdown', productRows)

  // ---------------------------------------------------------------------------
  // 4. LEADS PENDING FOLLOW-UP SHEET
  // ---------------------------------------------------------------------------
  const followUpRows = [
    ...createReportHeader({
      title: 'Leads Pending Follow-up',
      subtitle:
        'Active Leads Awaiting Contact or Action by Sales Representatives',
      period,
      customRange,
      exportedAt,
    }),
    ['ACTIONABLE FOLLOW-UP QUEUE'],
    [
      '#',
      'Lead Name',
      'Company',
      'Assigned Salesperson',
      'Lead Source',
      'Interested Products',
      'Product Quantities',
      'Email Address',
      'Phone Number',
      'Status / Pending Info',
    ],
  ]

  if (!Array.isArray(leadsNotFollowedUp) || leadsNotFollowedUp.length === 0) {
    followUpRows.push([
      '—',
      'All leads followed up — queue is empty',
      '—',
      '—',
      '—',
      '—',
      '—',
      '—',
      '—',
      '—',
    ])
  } else {
    leadsNotFollowedUp.forEach((lead, index) => {
      const salesName = lead.salesPerson?.name ?? lead.owner ?? 'Unassigned'
      const productNames =
        formatProductNames(lead.products) || lead.product || '—'
      const quantities = formatProductQuantities(lead.products) || '—'

      followUpRows.push([
        index + 1,
        lead.name || '—',
        lead.company || '—',
        salesName,
        lead.source || lead.platform || '—',
        productNames,
        quantities,
        lead.email || '—',
        lead.phone || '—',
        lead.pending || lead.status || 'Pending Follow-up',
      ])
    })
  }

  appendStyledSheet(workbook, 'Pending Follow-ups', followUpRows)

  // ---------------------------------------------------------------------------
  // Write Workbook
  // ---------------------------------------------------------------------------
  const stamp = new Date().toISOString().slice(0, 10)
  XLSX.writeFile(workbook, `admin-dashboard-${stamp}.xlsx`)
}
